import {
  readUserGroupsFromStorage,
  userSatisfiesPolicyKey,
  privAdminSatisfiesAllowedRoles,
  readUserIsPrivAdminFromStorage,
} from "utils/groupPolicyEngine";
import {
  findApplyingRestrictedRules,
  isCatalogScope,
  isAlwaysAccessiblePath,
  isAlwaysAccessibleRouteConfig,
  resolveCatalogScopeForPath,
  resolveCatalogScopeForRouteConfig,
  resolveRouteConfigAccessKeys,
  restrictedRuleAppliesToPath,
  routePathToSecAppName,
} from "utils/routeAccessRegistry";

export {
  routePathToSecAppName,
  scopeAppliesToPath,
  resolvePathAccessKeys,
  restrictedRuleAppliesToPath,
  findApplyingRestrictedRules,
  resolveCatalogScopeForPath,
  resolveCatalogScopeForRouteConfig,
  isCatalogScope,
  isAlwaysAccessiblePath,
  isAlwaysAccessibleRouteConfig,
} from "utils/routeAccessRegistry";

export { readUserIsPrivAdminFromStorage as readUserIsPrivAdmin } from "utils/groupPolicyEngine";

/**
 * @param {string|null|undefined} secAppName
 * @returns {RegExp|null}
 */
export function secAppNameToPathRegex(secAppName) {
  const normalized = routePathToSecAppName(secAppName);
  if (!normalized) return null;
  const pattern = normalized.replace(/:[^/]+/g, "[^/]+").replace(/\*/g, ".*");
  return new RegExp(`^${pattern}$`);
}

/**
 * @param {string} pathname
 * @param {string} secAppName
 * @returns {boolean}
 */
export function pathnameMatchesSecAppName(pathname, secAppName) {
  const regex = secAppNameToPathRegex(secAppName);
  if (!regex) return false;
  return regex.test(routePathToSecAppName(pathname));
}

/**
 * @param {string} pathname
 * @param {string[]} restrictedAppNames
 * @returns {string|null}
 */
export function findRestrictedSecAppForPath(pathname, restrictedAppNames) {
  const catalogScope = resolveCatalogScopeForPath(pathname);
  if (catalogScope) {
    return catalogScope;
  }

  const applying = findApplyingRestrictedRules(pathname, restrictedAppNames);
  return applying.length > 0 ? applying[0] : null;
}

/**
 * @param {unknown} value
 * @returns {'Y'|'N'|null}
 */
export function normalizePrivAccess(value) {
  const normalized = String(value || "").trim().toUpperCase();
  if (normalized === "Y") return "Y";
  if (normalized === "N") return "N";
  return null;
}

/**
 * Evaluate access for a catalog scope. Deny (N) from any group wins over allow (Y).
 * Unlisted scopes default to deny unless explicitly allowed.
 *
 * @param {string|null|undefined} scope
 * @param {{ userAllowedSecApps?: string[], userDeniedSecApps?: string[] }} context
 * @returns {boolean}
 */
export function evaluateCatalogScopeAccess(
  scope,
  { userAllowedSecApps = [], userDeniedSecApps = [] } = {}
) {
  const normalizedScope = routePathToSecAppName(scope);
  if (!normalizedScope || !isCatalogScope(normalizedScope)) {
    return true;
  }

  const deniedSet = new Set(
    (Array.isArray(userDeniedSecApps) ? userDeniedSecApps : []).map((value) =>
      routePathToSecAppName(value)
    )
  );
  const allowedSet = new Set(
    (Array.isArray(userAllowedSecApps) ? userAllowedSecApps : []).map((value) =>
      routePathToSecAppName(value)
    )
  );

  if (deniedSet.has(normalizedScope)) {
    return false;
  }

  return allowedSet.has(normalizedScope);
}

/**
 * Evaluate scoped access for pathname rules.
 * Deny (N) from any of the user's groups wins over allow (Y).
 *
 * @param {string[]} applyingRules
 * @param {{ userAllowedSecApps?: string[], userDeniedSecApps?: string[] }} context
 * @returns {boolean}
 */
export function evaluateApplyingRulesAccess(
  applyingRules,
  { userAllowedSecApps = [], userDeniedSecApps = [] } = {}
) {
  const rules = Array.isArray(applyingRules) ? applyingRules : [];
  if (rules.length < 1) {
    return true;
  }

  const deniedSet = new Set(
    (Array.isArray(userDeniedSecApps) ? userDeniedSecApps : []).map((value) =>
      routePathToSecAppName(value)
    )
  );
  const allowedSet = new Set(
    (Array.isArray(userAllowedSecApps) ? userAllowedSecApps : []).map((value) =>
      routePathToSecAppName(value)
    )
  );

  if (rules.some((rule) => deniedSet.has(routePathToSecAppName(rule)))) {
    return false;
  }

  return rules.some((rule) => allowedSet.has(routePathToSecAppName(rule)));
}

/**
 * @deprecated Use evaluateCatalogScopeAccess or evaluateApplyingRulesAccess
 */
export function userHasGrantForApplyingRules(applyingRules, userAllowedSecApps) {
  return evaluateApplyingRulesAccess(applyingRules, { userAllowedSecApps });
}

/**
 * Secondary route access via sec_groups_apps (primary policy checks run first).
 */
export function userCanAccessSecAppRoute({
  route,
  secAppName,
  secScope,
  restrictedSecApps = [],
  userAllowedSecApps = [],
  userDeniedSecApps = [],
}) {
  if (isAlwaysAccessibleRouteConfig({ route, secAppName, secScope }) || isAlwaysAccessiblePath(route)) {
    return true;
  }

  if (readUserIsPrivAdminFromStorage()) {
    return true;
  }

  const catalogScope = resolveCatalogScopeForRouteConfig({ route, secAppName, secScope });
  if (catalogScope) {
    return evaluateCatalogScopeAccess(catalogScope, {
      userAllowedSecApps,
      userDeniedSecApps,
    });
  }

  const probePaths = [];
  if (route) probePaths.push(route);
  if (secAppName) probePaths.push(secAppName);

  const routeKeys = resolveRouteConfigAccessKeys({ route, secAppName, secScope });
  const restricted = Array.isArray(restrictedSecApps) ? restrictedSecApps : [];

  const applying = new Set();
  probePaths.forEach((path) => {
    findApplyingRestrictedRules(path, restricted).forEach((rule) => applying.add(rule));
  });
  routeKeys.forEach((key) => {
    if (restricted.some((rule) => routePathToSecAppName(rule) === key)) {
      applying.add(key);
    }
  });

  const applyingRules = Array.from(applying);
  if (applyingRules.length < 1) {
    return true;
  }

  return evaluateApplyingRulesAccess(applyingRules, {
    userAllowedSecApps,
    userDeniedSecApps,
  });
}

/**
 * Central pathname gate — used by SecAppRouteGate for every protected page.
 * Catalog scopes default to deny unless the user has an explicit Allow from a group.
 *
 * @param {string} pathname
 * @param {{ restrictedSecApps?: string[], userAllowedSecApps?: string[], userDeniedSecApps?: string[] }} context
 * @returns {boolean}
 */
export function userCanAccessPathname(pathname, context = {}) {
  if (isAlwaysAccessiblePath(pathname)) {
    return true;
  }

  if (readUserIsPrivAdminFromStorage()) {
    return true;
  }

  const catalogScope = resolveCatalogScopeForPath(pathname);
  if (catalogScope) {
    return evaluateCatalogScopeAccess(catalogScope, {
      userAllowedSecApps: context.userAllowedSecApps || [],
      userDeniedSecApps: context.userDeniedSecApps || [],
    });
  }

  const restricted = context.restrictedSecApps || [];
  const applyingRules = findApplyingRestrictedRules(pathname, restricted);
  if (applyingRules.length < 1) {
    return true;
  }

  return evaluateApplyingRulesAccess(applyingRules, {
    userAllowedSecApps: context.userAllowedSecApps || [],
    userDeniedSecApps: context.userDeniedSecApps || [],
  });
}

/**
 * @param {Array<{ group_id?: unknown }>|null|undefined} userGroups
 * @returns {number[]}
 */
export function readUserGroupIds(userGroups) {
  const groups = userGroups ?? readUserGroupsFromStorage();
  return (groups || [])
    .map((row) => parseInt(String(row?.group_id ?? "").trim(), 10))
    .filter((value) => Number.isFinite(value) && value > 0);
}

/**
 * Build restricted + allowed app_name lists from sec_groups_apps rows.
 * Only catalog scopes participate in default-deny enforcement.
 */
export function buildSecAppAccessFromRows(rows, userGroupIds) {
  const list = Array.isArray(rows) ? rows : [];
  const groupIdSet = new Set(userGroupIds || []);
  const restrictedSet = new Set();
  const allowedSet = new Set();
  const deniedSet = new Set();

  list.forEach((row) => {
    const appName = routePathToSecAppName(row?.app_name);
    if (!appName || !isCatalogScope(appName)) return;

    const privAccess = normalizePrivAccess(row?.priv_access);
    if (privAccess) {
      restrictedSet.add(appName);
    }

    const groupId = parseInt(String(row?.group_id ?? "").trim(), 10);
    if (!groupIdSet.has(groupId)) return;

    if (privAccess === "Y") {
      allowedSet.add(appName);
    } else if (privAccess === "N") {
      deniedSet.add(appName);
    }
  });

  return {
    restrictedSecApps: Array.from(restrictedSet).sort(),
    userAllowedSecApps: Array.from(allowedSet).sort(),
    userDeniedSecApps: Array.from(deniedSet).sort(),
  };
}

/**
 * Primary policy + secondary sec_groups_apps check for sidenav/help routes.
 */
export function canViewRouteWithSecApps(route, context = {}) {
  if (!route || route.hidden) {
    return false;
  }

  if (isAlwaysAccessibleRouteConfig(route)) {
    return true;
  }

  if (Array.isArray(route.collapse) && route.collapse.length > 0) {
    const hasVisibleChild = route.collapse.some(
      (child) => !child.hidden && canViewRouteWithSecApps(child, context)
    );
    if (hasVisibleChild) {
      return true;
    }
  }

  const userGroups = context.userGroups ?? readUserGroupsFromStorage();
  const policyKey = route?.allowedPolicy;
  if (typeof policyKey === "string" && policyKey.trim()) {
    if (!userSatisfiesPolicyKey(userGroups, policyKey.trim())) {
      return false;
    }
  } else {
    const allowedRoles = route?.allowedRoles;
    if (Array.isArray(allowedRoles) && allowedRoles.length > 0) {
      const userRoles = context.userRoles || [];
      const roleAllowed = allowedRoles.some((role) => userRoles.includes(role));
      if (!roleAllowed && !privAdminSatisfiesAllowedRoles(allowedRoles)) {
        return false;
      }
    }
  }

  return userCanAccessSecAppRoute({
    route: route?.route,
    secAppName: route?.secAppName,
    secScope:
      route?.secScope ||
      (Array.isArray(route?.collapse) && route?.key && !route?.route ? route.key : undefined),
    restrictedSecApps: context.restrictedSecApps,
    userAllowedSecApps: context.userAllowedSecApps,
    userDeniedSecApps: context.userDeniedSecApps,
  });
}
